import React, { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Calendar, Clock, User, ArrowLeft, ArrowRight, MessageCircle } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { BLOG_POSTS } from '../data/blogData';
export function BlogPost() {
  const {
    id
  } = useParams<{
    id: string;
  }>();
  const post = BLOG_POSTS.find(p => p.id === Number(id));
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);
  if (!post) {
    return <div className="min-h-screen bg-brand-light flex flex-col items-center justify-center text-center px-4 py-32" data-id="element-469">
        <h1 className="text-3xl font-heading font-bold text-brand-navy mb-4" data-id="element-470">
          Article not found
        </h1>
        <p className="text-slate-600 mb-8" data-id="element-471">
          The article you are looking for doesn't exist or has been moved.
        </p>
        <Button href="/blog" variant="primary" icon={<ArrowLeft className="w-5 h-5" data-id="element-473" />} data-id="element-472">
          Back to Blog
        </Button>
      </div>;
  }
  const related = BLOG_POSTS.filter(p => p.id !== post.id).slice(0, 3);
  return <div className="min-h-screen bg-brand-light pb-20" data-id="element-474">
      {/* Hero */}
      <div className="relative bg-brand-navy" data-id="element-475">
        <img src={post.image} alt={post.title} className="absolute inset-0 w-full h-full object-cover" data-id="element-476" />
        <div className="absolute inset-0 bg-gradient-to-r from-brand-navy/95 via-brand-navy/80 to-brand-navy/50" data-id="element-477" />
        <div className="relative z-10 max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-24" data-id="element-478">
          <Link to="/blog" className="inline-flex items-center gap-2 text-slate-200 hover:text-white text-sm font-medium mb-6 transition-colors" data-id="element-479">
            <ArrowLeft className="w-4 h-4" data-id="element-480" /> Back to Blog
          </Link>
          <span className="inline-block bg-brand-red text-white text-xs font-bold uppercase tracking-wider py-1 px-3 rounded-full mb-4" data-id="element-481">
            {post.category}
          </span>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-white leading-tight mb-6" data-id="element-482">
            {post.title}
          </h1>
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-slate-200 text-sm" data-id="element-483">
            <span className="flex items-center gap-2" data-id="element-484">
              <Calendar className="w-4 h-4 text-brand-gold" data-id="element-485" /> {post.date}
            </span>
            <span className="flex items-center gap-2" data-id="element-486">
              <Clock className="w-4 h-4 text-brand-gold" data-id="element-487" /> {post.readTime}
            </span>
            <span className="flex items-center gap-2" data-id="element-488">
              <User className="w-4 h-4 text-brand-gold" data-id="element-489" /> {post.author}
            </span>
          </div>
        </div>
      </div>

      {/* Article */}
      <article className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 mt-12" data-id="element-490">
        <div className="bg-white rounded-3xl shadow-soft border border-slate-100 p-8 md:p-12" data-id="element-491">
          {post.content.map((block, idx) => {
          if (block.type === 'heading') {
            return <h2 key={idx} className="text-2xl md:text-3xl font-heading font-bold text-brand-navy mt-10 mb-4 first:mt-0" data-id="element-492">
                  {block.text}
                </h2>;
          }
          if (block.type === 'subheading') {
            return <h3 key={idx} className="text-lg md:text-xl font-heading font-bold text-brand-red mt-6 mb-3" data-id="element-493">
                  {block.text}
                </h3>;
          }
          if (block.type === 'list') {
            return <ul key={idx} className="space-y-2 mb-6" data-id="element-494">
                  {block.items.map(item => <li key={item} className="flex items-start gap-3" data-id="element-495">
                      <span className="w-1.5 h-1.5 rounded-full bg-brand-gold mt-2.5 shrink-0" data-id="element-496" />
                      <span className="text-slate-600 leading-relaxed" data-id="element-497">
                        {item}
                      </span>
                    </li>)}
                </ul>;
          }
          return <p key={idx} className="text-slate-600 text-lg leading-relaxed mb-5" data-id="element-498">
                {block.text}
              </p>;
        })}

          {/* In-article CTA */}
          <div className="mt-10 rounded-2xl bg-brand-navy p-8 text-center" data-id="element-499">
            <h3 className="text-xl font-heading font-bold text-white mb-2" data-id="element-500">
              Your Health Is Our Devotion
            </h3>
            <p className="text-slate-200 text-sm mb-6" data-id="element-501">
              Book an appointment or chat with us to discuss your health
              concerns.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center" data-id="element-502">
              <Button href="/book" variant="primary" data-id="element-503">
                Book an Appointment
              </Button>
              <a href="https://wa.me/26773241613" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 rounded-full bg-[#25D366] text-white font-medium px-6 py-3 hover:bg-[#20bd5a] transition-colors" data-id="element-504">
                <MessageCircle className="w-5 h-5" data-id="element-505" />
                Chat on WhatsApp
              </a>
            </div>
          </div>
        </div>
      </article>

      {/* Related */}
      <section className="max-w-brand mx-auto px-4 sm:px-6 lg:px-8 mt-20" data-id="element-506">
        <h2 className="text-2xl font-heading font-bold text-brand-navy mb-8" data-id="element-507">
          More Health Insights
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-508">
          {related.map(p => <Link key={p.id} to={`/blog/${p.id}`} className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-soft-lg transition-all duration-300 border border-slate-100 group flex flex-col" data-id="element-509">
              <div className="h-44 overflow-hidden" data-id="element-510">
                <img src={p.image} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" data-id="element-511" />
              </div>
              <div className="p-6 flex flex-col flex-grow" data-id="element-512">
                <span className="text-xs font-bold uppercase tracking-wider text-brand-red mb-2" data-id="element-513">
                  {p.category}
                </span>
                <h3 className="text-lg font-heading font-bold text-brand-navy mb-3 group-hover:text-brand-red transition-colors line-clamp-2" data-id="element-514">
                  {p.title}
                </h3>
                <span className="inline-flex items-center gap-2 text-brand-navy font-semibold text-sm mt-auto group-hover:text-brand-red transition-colors" data-id="element-515">
                  Read More <ArrowRight className="w-4 h-4" data-id="element-516" />
                </span>
              </div>
            </Link>)}
        </div>
      </section>
    </div>;
}